import { useEffect } from 'react';
import { ArrowRightIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { business, deliveryBargeAlt, deliveryDockAlt, images, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Who Are Pegasus? | Family-Owned Since 1977 in Brooklyn, MI';
const DESCRIPTION = 'Meet Pegasus Spas, a family-owned hot tub, outdoor living and spa service showroom in Brooklyn, Michigan, serving local families since 1977.';
const CANONICAL = 'https://www.pegasus-spas.com/about_pegasus.html';
const moreLinks = [{
  label: 'Read Customer Reviews',
  href: routes.reviews
}, {
  label: 'Soak & Share Referrals',
  href: routes.referrals
}, {
  label: 'Service Work Guarantee',
  href: routes.serviceGuarantee
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-579" data-mp="32m1t41rdqvnw">
      {/* People first */}
      <section aria-labelledby="about-h1" className="w-full bg-white" data-id="element-580" data-mp="32l0e5m78tp0w">
        <div className="mx-auto max-w-4xl px-5 pb-10 pt-10 text-center sm:px-8 lg:pb-12 lg:pt-14" data-id="element-581" data-mp="2otc2tl4tj4kz">
          <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-582" data-mp="2ts6yhnnvwnmi">
            Family-Owned in Brooklyn Since 1977
          </p>
          <h1 id="about-h1" className="mt-2 font-display text-h1-mobile font-semibold text-ink lg:text-[52px] lg:leading-[60px]" data-id="element-583" data-mp="3hgzpd5png5a9">
            Who Are Pegasus?
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-body text-ink/80 sm:text-body-lg" data-id="element-584" data-mp="3w3f1lrq8qpen">
            We&rsquo;re a family-owned business on South Main Street in Brooklyn, Michigan, and
            we&rsquo;ve been helping local families enjoy their backyards since 1977. Stop in, say
            hello and ask us anything.
          </p>
        </div>

        <div className="mx-auto max-w-5xl px-5 pb-12 sm:px-8 lg:pb-16" data-id="element-585" data-mp="3d7yotpvj3w5e">
          <img src={images.teamConcept} alt="Temporary AI-generated team layout concept, labeled 'AI layout concept' and 'Updated photo needed'. Not a photograph of the current Pegasus team." className="block h-auto w-full rounded-card object-contain" decoding="async" data-id="element-586" data-mp="38fnhi1ygl82t" />
        </div>
      </section>

      {/* 1977 to today */}
      <section aria-labelledby="story-heading" className="w-full bg-water" data-id="element-587" data-mp="2phkxk1krq1bv">
        <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8 lg:py-16" data-id="element-588" data-mp="2pjfgxbge5ldu">
          <h2 id="story-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-589" data-mp="14yqvaoxvzguk">
            From 1977 to Today
          </h2>
          <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-590" data-mp="1e0i0lsoillnw">
            Pegasus started in Brooklyn in 1977, and we&rsquo;re still family-owned and still in
            the same town. Today you can come to us for hot tubs, outdoor furniture,
            Big Green Egg grills, covers, supplies and help from our own service department.
          </p>
          <p className="mt-3 text-body text-ink/80 sm:text-body-lg" data-id="element-591" data-mp="v9qs0tccpzak">
            We carry brands we know well, including Sundance Spas and Viking Spas, and we stay
            with our customers long after delivery day, whether that means a water test, a part or
            a service visit.
          </p>
        </div>
      </section>

      {/* Real work */}
      <section aria-labelledby="work-heading" className="w-full bg-white" data-id="element-592" data-mp="7i1umykoakmr">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-593" data-mp="299vedvi19o50">
          <div className="mx-auto max-w-3xl text-center" data-id="element-594" data-mp="2589v9sewodmd">
            <h2 id="work-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-595" data-mp="255nmxqk6xu33">
              We&rsquo;ll Find a Way to Get It There
            </h2>
            <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-596" data-mp="2swjk737f1hhq">
              When a lake cottage could only be reached from the water, we loaded the spa onto a
              barge and took it across the lake.
            </p>
          </div>

          <figure className="mt-8" data-id="element-597" data-mp="31wy88abrlaox">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12" data-id="element-598" data-mp="2wywf0wkmjuzq">
              <div className="relative aspect-[4/3] overflow-hidden rounded-card sm:col-span-7" data-id="element-599" data-mp="2wwjxulmoxonq">
                <img src={images.deliveryDock} alt={deliveryDockAlt} className="absolute inset-0 h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-600" data-mp="17lsxyn66a00m" />
              </div>
              <div className="relative aspect-[4/3] overflow-hidden rounded-card sm:col-span-5 sm:aspect-auto" data-id="element-601" data-mp="3j966znnld3yn">
                <img src={images.deliveryBarge} alt={deliveryBargeAlt} className="absolute inset-0 h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-602" data-mp="35dubvf5ixbh7" />
              </div>
            </div>
            <figcaption className="mt-3 text-center text-[15px] leading-6 text-ink/65" data-id="element-603" data-mp="2mh2nwsf1e86t">
              A past Pegasus lake-access delivery.
            </figcaption>
          </figure>

          <ul className="mt-8 flex flex-col items-center gap-x-8 gap-y-0 sm:flex-row sm:flex-wrap sm:justify-center" data-id="element-604" data-mp="2qk7kyfgclho2">
            {moreLinks.map(link => <li key={link.label} data-id="element-605" data-mp="opq60ce8hjtf">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-606" data-mp="ooowiccv95dt">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-607" data-mp="1jq9v4uwurscr" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Showroom CTA */}
      <section aria-labelledby="visit-heading" className="w-full border-t border-ink/10 bg-water" data-id="element-608" data-mp="2ceyy6zhgy7r5">
        <div className="mx-auto max-w-3xl px-5 py-12 text-center sm:px-8 lg:py-14" data-id="element-609" data-mp="27k1ft1yaum1l">
          <h2 id="visit-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-610" data-mp="22msx9098ofzt">
            Come See Us in Brooklyn
          </h2>
          <address className="mt-4 not-italic text-body text-ink/80 sm:text-body-lg" data-id="element-611" data-mp="2vafcj5rtx92i">
            {business.street}, {business.cityStateZip}
          </address>
          <p className="mt-1 text-[16px] leading-6 text-ink/70" data-id="element-612" data-mp="37l6cqu8n9ryt">
            {business.hours.map(entry => `${entry.days} ${entry.time}`).join(' · ')}
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3" data-id="element-613" data-mp="3gmo10zw7gzyl">
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-614" data-mp="3gkjojtk8qsmp">
              <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-615" data-mp="3vcbytr56hghk" />
              Plan Your Visit
            </SiteLink>
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-616" data-mp="47263rpsuo49">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-617" data-mp="tht26dwpu8vo" />
              Call or Text {business.phone}
            </a>
          </div>
        </div>
      </section>
    </main>;
}